/**
 * Text diagnostics panel (F3). Updated at a fixed low rate to stay cheap.
 */
import type { Simulation } from '../simulation/Simulation';
import type { PerfSnapshot } from './PerfMonitor';
import type { RenderStats } from '../rendering/Renderer';

const UPDATE_INTERVAL_MS = 100;

export class DebugOverlay {
  private last = 0;
  visible = false;

  constructor(private readonly el: HTMLElement) {}

  setVisible(v: boolean): void {
    this.visible = v;
    this.el.hidden = !v;
  }

  update(nowMs: number, sim: Simulation, perf: PerfSnapshot, render: RenderStats, paused: boolean): void {
    if (!this.visible || nowMs - this.last < UPDATE_INTERVAL_MS) return;
    this.last = nowMs;
    const st = sim.state;
    const m = sim.motor.diagnostics;
    const mo = m.motion;
    const f = (n: number, d = 2): string => n.toFixed(d);
    const v = st.velocity;
    const vUp = v.dot(st.up);
    const ground = st.grounded ? `GROUND (${st.groundMaterial?.id ?? '?'}, ${f((Math.acos(Math.min(1, st.groundNormal.dot(st.up))) * 180) / Math.PI, 1)}°)` : 'AIR';
    const contacts = [];
    for (let i = 0; i < st.contactCount; i++) contacts.push(`${st.contacts[i]!.materialId}@${f(st.contacts[i]!.distance, 3)}`);
    const timer = sim.attempts.timer.elapsed(st.tick);
    const lines = [
      `XERNOUS MOVEMENT LAB — diagnostics (F3)${paused ? '  [PAUSED]' : ''}`,
      `render  ${f(perf.fps, 0)} fps  frame avg ${f(perf.frameAvgMs)} ms  worst ${f(perf.frameWorstMs)} ms  draw ${f(perf.renderAvgMs)} ms`,
      `gpu     calls ${render.drawCalls}  tris ${render.triangles}  geo ${render.geometries}  tex ${render.textures}`,
      `sim     ${sim.config.simulation.tickRate} Hz  step avg ${f(perf.simStepAvgMs, 3)} ms  max ${f(perf.simStepMaxMs, 3)} ms  catch-up max ${perf.catchUpMax}  stalls ${perf.stallCount}`,
      `queries ${f(perf.queriesPerTick, 1)}/tick  ${f(perf.queryAvgMsPerTick, 3)} ms/tick  colliders ${sim.level.physics.colliderCount}`,
      `memory  ${perf.heapMB === null ? 'n/a (browser does not expose heap)' : `${f(perf.heapMB, 1)} MB JS heap`}`,
      ``,
      `tick    ${st.tick}   state ${st.alive ? ground : `DEAD (${st.deathCause})`}`,
      `pos     ${f(st.position.x)} ${f(st.position.y)} ${f(st.position.z)}`,
      `vel     ${f(v.x)} ${f(v.y)} ${f(v.z)}   |v| ${f(st.speed())}  planar ${f(st.planarSpeed())}  vertical ${f(vUp)}`,
      `desired ${f(mo.desired.x, 4)} ${f(mo.desired.y, 4)} ${f(mo.desired.z, 4)}   resolved Δ ${f(st.position.x - st.previousPosition.x, 4)} ${f(st.position.y - st.previousPosition.y, 4)} ${f(st.position.z - st.previousPosition.z, 4)}`,
      `slide   iterations ${mo.iterations}  hits ${mo.hitCount}  depenetration ${f(mo.depenetration, 4)} m`,
      `contact ${contacts.join('  ') || '—'}`,
      `rules   ${sim.motor.rules.enabledIds().join(', ') || 'baseline only'}  violations ${m.ruleViolations}${m.lastRuleViolation ? ` (${m.lastRuleViolation})` : ''}  faults ${m.numericalFaults}`,
      `attempt #${sim.attempts.attempt} ${sim.mode} ${sim.attempts.status}  t ${timer === null ? '—' : f(timer, 3)} s  cp ${sim.attempts.lastCheckpointIndex ?? '—'}`,
      `legend  yellow velocity · magenta desired · green resolved · white/red hits · cyan ground · blue contacts`,
      `dev     B: +15 m/s along view (dev builds; invalidates full runs)`,
    ];
    this.el.textContent = lines.join('\n');
  }
}
